/**
 * Enrichment contacts download primitives — TD-2101 AC-7/AC-8 (v3).
 *
 * The v3 XLSX export is async: when the user ticks enrichment fields and the
 * BE has to call Surfe, the export endpoint returns `202 + jobId` instead of a
 * binary. These primitives are scope-agnostic (company + project) and pure
 * w.r.t. URL building so the orchestration hook can compose them.
 *
 * BE contract (td-backend/develop, lambdas-auxbe/lib/contactEnrichmentExport.js):
 *   query: enrichmentFields=personal_email,mobile_phone  (comma-joined; keys are
 *          ENRICHMENT_FIELDS — NOT the dialog's `email`/`phone` keys),
 *          mode=fetch_missing|cached_only (default fetch_missing),
 *          skipPreviouslyEnriched=true|false
 *   200 binary  → empty fields, OR mode=cached_only, OR nothing left to fetch
 *   202 JSON    → { jobId, status, totalContacts, estimatedCost, surfeEnrichmentId }
 *   402 JSON    → { errorMessage, required, available }
 */

import { extractFilename, triggerDownload } from './downloadUtils';

// Dialog field keys → BE `enrichmentFields` param values. The dialog tracks
// `email`/`phone`; the export endpoint keys on `personal_email`/`mobile_phone`
// (lib/contactsDataExport.js ENRICHMENT_FIELDS).
export const ENRICHMENT_FIELD_PARAM_MAP = {
  email: 'personal_email',
  phone: 'mobile_phone'
};

export const EXPORT_MODE = {
  FETCH_MISSING: 'fetch_missing',
  CACHED_ONLY: 'cached_only'
};

/**
 * TD-1908 regression gate: only the contacts XLSX download opens the v3 dialog,
 * and only for enrichment-enabled users. Every other download type — and every
 * non-enrichment user — falls through to the legacy immediate download.
 *
 * Demo accounts are excluded on purpose. /subscription-status DOES return an
 * enrichment block for a demo user in the `contactenrichment` group (the fixed
 * per-user demo budget), so `enrichmentEnabled` is true for them and the dialog
 * would offer the enrichment fields — but the BE rejects any contacts export
 * carrying `enrichmentFields` from a demo caller with a flat 403 ("contact
 * downloads are disabled for demo accounts"). Routing demo to the legacy plain
 * export makes that request unconstructable instead of letting the user build it
 * and fail. Reveal in the app is unaffected; only the file export is.
 *
 * @param {string} downloadType
 * @param {boolean} enrichmentEnabled - useEnrichmentAccess().enabled
 * @param {Object} [options]
 * @param {boolean} [options.isDemo=false] - profilerAccessStore.isProfilerDemo
 * @returns {boolean}
 */
export const shouldOpenEnrichmentDownloadDialog = (
  downloadType,
  enrichmentEnabled,
  { isDemo = false } = {}
) => downloadType === 'contacts' && enrichmentEnabled === true && !isDemo;

/**
 * Maps dialog field keys to the comma-joined BE `enrichmentFields` param.
 * Unknown keys are dropped (mirrors the BE's silent-drop of unsupported fields).
 *
 * @param {string[]} dialogFields - e.g. ['email', 'phone']
 * @returns {string} e.g. 'personal_email,mobile_phone' ('' when empty)
 */
export const toEnrichmentFieldsParam = (dialogFields) => {
  if (!Array.isArray(dialogFields)) return '';
  return dialogFields
    .map((f) => ENRICHMENT_FIELD_PARAM_MAP[f])
    .filter(Boolean)
    .join(',');
};

/**
 * Appends the v3 enrichment query params to an export URL.
 *
 * The enrichment params (fields/mode/skip) are added only when fields are
 * selected — with none selected that is the legacy base-fields-only export
 * (200 binary, zero credits), so we must NOT send mode/fields.
 *
 * The TD-2332 `roleBuckets` filter is orthogonal: it narrows WHICH contacts the
 * export contains, so it is appended whenever a filter is active — including a
 * base-only download (the BE filters rows before rendering, regardless of
 * enrichment fields). When neither fields nor a role filter are set the URL is
 * returned unchanged (pure legacy path).
 *
 * @param {string} urlString - base export URL (already carries companyId/projectId)
 * @param {{ enrichmentFields?: string[], mode?: string, skipPreviouslyEnriched?: boolean, roleBuckets?: string[] }} params
 * @returns {string}
 */
export const appendEnrichmentParams = (
  urlString,
  {
    enrichmentFields = [],
    mode = EXPORT_MODE.FETCH_MISSING,
    skipPreviouslyEnriched = true,
    roleBuckets = []
  } = {}
) => {
  const fieldsParam = toEnrichmentFieldsParam(enrichmentFields);
  const hasRoleFilter = Array.isArray(roleBuckets) && roleBuckets.length > 0;
  if (!fieldsParam && !hasRoleFilter) return urlString;

  const url = new URL(urlString);
  if (fieldsParam) {
    url.searchParams.set('enrichmentFields', fieldsParam);
    url.searchParams.set('mode', mode);
    url.searchParams.set('skipPreviouslyEnriched', String(skipPreviouslyEnriched));
  }
  if (hasRoleFilter) url.searchParams.set('roleBuckets', roleBuckets.join(','));
  return url.toString();
};

// Shared status classification for both export scopes. The 202 (Surfe job
// kicked off) and 402 (insufficient credits) paths are identical across the
// company and project endpoints — only the 200 success body differs. Returns
// `{ status: 'ok' }` for a 200 so the caller applies its scope-specific
// binary-retrieval strategy.
const classifyExportResponse = async (response) => {
  if (response.status === 202) {
    const data = await response.json().catch(() => ({}));
    return { status: 'pending', jobId: data?.jobId ?? null };
  }
  if (response.status === 402) {
    const data = await response.json().catch(() => ({}));
    return {
      status: 'insufficient',
      required: data?.required ?? null,
      available: data?.available ?? null
    };
  }
  if (!response.ok) return { status: 'error' };
  return { status: 'ok' };
};

/**
 * Status-aware export request for the COMPANY scope
 * (`/export-profiler-company-contacts`), which returns the XLSX binary
 * directly (base64 + Content-Disposition). Reads the status BEFORE consuming
 * the body so a 202 isn't mistakenly parsed as a binary blob.
 *
 * @returns {Promise<
 *   { status: 'downloaded' } |
 *   { status: 'pending', jobId: string } |
 *   { status: 'insufficient', required: number, available: number } |
 *   { status: 'error' }
 * >}
 */
export const requestEnrichmentExport = async ({
  url,
  token,
  defaultFilename,
  defaultContentType,
  linkId = 'download-contacts-btn'
}) => {
  if (!token) return { status: 'error' };

  try {
    const response = await fetch(url, {
      headers: { Authorization: token, 'Content-Type': 'application/json' }
    });

    const classified = await classifyExportResponse(response);
    if (classified.status !== 'ok') return classified;

    // 200 → binary XLSX.
    const contentDisposition = response.headers.get('Content-Disposition');
    const contentType = response.headers.get('Content-Type') || defaultContentType;
    const filename = extractFilename(contentDisposition, defaultFilename);
    const arrayBuffer = await response.arrayBuffer();
    triggerDownload(new Blob([arrayBuffer], { type: contentType }), filename, linkId);
    return { status: 'downloaded' };
  } catch {
    return { status: 'error' };
  }
};

/**
 * Status-aware export request for the PROJECT scope
 * (`/export-contacts-list`), which returns `{ url }` (an S3 pre-signed URL)
 * on 200 — the binary lives behind a second, unauthenticated fetch. The 202 /
 * 402 paths are identical to the company scope (shared BE handler).
 *
 * @returns {Promise<
 *   { status: 'downloaded' } |
 *   { status: 'pending', jobId: string } |
 *   { status: 'insufficient', required: number, available: number } |
 *   { status: 'error' }
 * >}
 */
export const requestEnrichmentExportViaUrl = async ({
  url,
  token,
  defaultFilename,
  linkId = 'download-contacts-btn'
}) => {
  if (!token) return { status: 'error' };

  try {
    const response = await fetch(url, {
      headers: { Authorization: token, 'Content-Type': 'application/json' }
    });

    const classified = await classifyExportResponse(response);
    if (classified.status !== 'ok') return classified;

    // 200 → presigned { url }. Fetch the binary from S3 (no auth header).
    // Tolerate both the flat { url } body and the { success, data: { url } }
    // envelope so a BE response-shape change can't silently break the download.
    const data = await response.json().catch(() => ({}));
    const fileUrl = data?.data?.url ?? data?.url;
    if (!fileUrl) return { status: 'error' };

    const fileResponse = await fetch(fileUrl);
    // S3 presigned URLs can expire (TTL) or 404 if the object was already
    // GC'd. Without this check the HTML error-page body becomes a corrupt
    // .xlsx delivered to the user with no toast. (Code-review fix #2.)
    if (!fileResponse.ok) return { status: 'error' };
    const contentDisposition = fileResponse.headers.get('Content-Disposition');
    const filename = extractFilename(contentDisposition, defaultFilename);
    const blob = await fileResponse.blob();
    triggerDownload(blob, filename, linkId);
    return { status: 'downloaded' };
  } catch {
    return { status: 'error' };
  }
};
